import {
  ChecklistItem,
  ChecklistItemCustomInput,
} from '../types/ChecklistTypes';
import { ChecklistItemStatus } from '../types/ChecklistItemStatus.enum';
import { parseCustomInputContent } from '../../components/fields/custom-inputs/custom-input.parser';
import { parseZonedTimestampMs } from '../../../utils/timezone.utils';

export type RepeatableInputPayload =
  | { value: string }
  | { selected: string | string[] };

export function repeatableInputValues(
  items: ChecklistItem[],
  prototype: ChecklistItem
): Record<string, RepeatableInputPayload> {
  const groupId = prototype.repeatableGroupId ?? prototype.id;
  const source = items
    .filter(
      item =>
        !item.isRepeatablePrototype &&
        item.repeatableGroupId === groupId &&
        item.status === ChecklistItemStatus.Completed &&
        parseZonedTimestampMs(item.statusChanged) !== null
    )
    .sort(
      (a, b) =>
        parseZonedTimestampMs(b.statusChanged)! -
          parseZonedTimestampMs(a.statusChanged)! ||
        (b.repeatableOccurrencePosition ?? 0) -
          (a.repeatableOccurrencePosition ?? 0) ||
        a.id.localeCompare(b.id)
    )[0];
  const values: Record<string, RepeatableInputPayload> = {};
  if (!source) return values;
  for (const input of prototype.customInputs ?? []) {
    const config = parseCustomInputContent(
      input.customInputTypeId,
      input.inputContent
    );
    if (
      config.repeatValue !== 'lastDone' ||
      (config.type !== 'string_field' && config.type !== 'dropdown')
    )
      continue;
    const previous = (source.customInputs ?? []).find(
      candidate =>
        candidate.customInputTypeId === input.customInputTypeId &&
        (candidate.sourceCustomInputId && input.sourceCustomInputId
          ? candidate.sourceCustomInputId === input.sourceCustomInputId
          : candidate.position === input.position)
    );
    const payload = readPayload(previous);
    if (!payload || Object.keys(payload).length !== 1) continue;
    if (
      config.type === 'string_field' &&
      typeof payload['value'] === 'string' &&
      payload['value'].trim()
    ) {
      values[input.id] = { value: payload['value'] };
    } else if (config.type === 'dropdown') {
      const selected = payload['selected'];
      const isOption = (value: unknown) =>
        config.options.some(option => option.value === value);
      if (config.dropdownType === 'multiple') {
        if (
          Array.isArray(selected) &&
          selected.length > 0 &&
          selected.every(isOption)
        )
          values[input.id] = { selected: selected as string[] };
      } else if (typeof selected === 'string' && isOption(selected)) {
        values[input.id] = { selected };
      }
    }
  }
  return values;
}

function readPayload(
  input: ChecklistItemCustomInput | undefined
): Record<string, unknown> | null {
  if (!input?.payload) return null;
  try {
    const payload: unknown =
      typeof input.payload === 'string'
        ? JSON.parse(input.payload)
        : input.payload;
    return payload && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}
